/**
 * @fileoverview Popover - Accessible floating popover components
 *
 * Purpose: Provides shadcn/ui popovers with full Radix UI accessibility
 * Dependencies: Radix UI Popover primitives
 * Used by: Context panels, pickers, additional info displays, dropdown content
 *
 * Components exported:
 * - Popover: Root container
 * - PopoverTrigger: Button that opens the popover
 * - PopoverContent: Floating content container
 *
 * Features:
 * - Click to open/close
 * - Automatic positioning (avoids viewport edges)
 * - Click outside to close
 * - ESC key to close
 * - Smooth fade and zoom animations
 * - Accessible (keyboard navigation, ARIA)
 * - Portal rendering (renders above other content)
 *
 * @example
 * <Popover>
 *   <PopoverTrigger asChild>
 *     <Button variant="outline">Open Popover</Button>
 *   </PopoverTrigger>
 *   <PopoverContent>
 *     <div className="space-y-2">
 *       <h4 className="font-medium">Dimensions</h4>
 *       <p className="text-sm text-muted-foreground">
 *         Set the dimensions for the component.
 *       </p>
 *     </div>
 *   </PopoverContent>
 * </Popover>
 *
 * @example
 * // Custom alignment
 * <Popover>
 *   <PopoverTrigger>Trigger</PopoverTrigger>
 *   <PopoverContent align="start" sideOffset={8}>
 *     Aligned to start with more offset
 *   </PopoverContent>
 * </Popover>
 */

"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/utils/misc";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

/**
 * Default portal container for popover content.
 *
 * When a popover is rendered inside a modal Radix Dialog / Sheet, portaling
 * its content to `document.body` (the default) places it *outside* the
 * dialog's `react-remove-scroll` subtree — which silently swallows wheel /
 * touch scrolling inside the popover (e.g. a long combobox list won't scroll).
 * Providers (like `SheetContent`) publish their content node here so popovers
 * portal *into* the dialog and stay scrollable. Defaults to `null` (body).
 */
const PopoverPortalContainerContext = React.createContext<HTMLElement | null>(
  null,
);

const PopoverPortalContainerProvider = PopoverPortalContainerContext.Provider;

/**
 * PopoverContent displays the floating popover content
 *
 * Features:
 * - Automatic positioning relative to trigger
 * - Portal rendering (above other content in DOM)
 * - Click outside to dismiss
 * - Smooth animations (fade, zoom, slide)
 * - Default width of 288px (customizable)
 * - Configurable alignment (start, center, end)
 * - Configurable side offset
 * - Focus management
 * - Accessible (ARIA attributes)
 *
 * @component
 * @example
 * <PopoverContent>
 *   <div className="space-y-2">
 *     <h4>Settings</h4>
 *     <p>Configure your preferences</p>
 *   </div>
 * </PopoverContent>
 *
 * @example
 * // Custom width and alignment
 * <PopoverContent className="w-96" align="start">
 *   Wider popover aligned to start
 * </PopoverContent>
 */
const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    container?: HTMLElement | null;
  }
>(
  (
    { className, align = "center", sideOffset = 4, container, ...props },
    ref,
  ) => {
    const contextContainer = React.useContext(PopoverPortalContainerContext);
    return (
      <PopoverPrimitive.Portal
        container={container ?? contextContainer ?? undefined}
      >
        <PopoverPrimitive.Content
          ref={ref}
          align={align}
          sideOffset={sideOffset}
          className={cn(
            "z-50 w-72 origin-[--radix-popover-content-transform-origin] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Portal>
    );
  },
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverAnchor = PopoverPrimitive.Anchor;

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverPortalContainerProvider,
};
